import { useEffect, useLayoutEffect, useState } from 'react';

interface Step {
  selector: string;
  title: string;
  body: string;
  /** Preferred callout placement relative to the highlighted element. */
  placement: 'bottom' | 'top' | 'left' | 'right';
}

/** Shown once per browser. Bump the suffix to re-show after major tool changes. */
const STORAGE_KEY = 'ksq-pdf-tour-v1';
const CALLOUT_W = 300;
const CALLOUT_H = 180; // estimate, for viewport clamping only
const PAD = 8; // spotlight padding around the target

const STEPS: Step[] = [
  {
    selector: '[data-tour="pdf-tools"]',
    placement: 'left',
    title: 'Mark up the page',
    body: 'Pick a tool: select text, drop a pin, draw a box or sketch freehand. The hand tool (or holding Space) pans the page; scroll to zoom.',
  },
  {
    selector: '[data-tour="pdf-comments"]',
    placement: 'left',
    title: 'Comments live here',
    body: 'Every mark opens a comment thread. Reply, resolve and @mention teammates from this panel.',
  },
  {
    selector: '[data-tour="pdf-review"]',
    placement: 'bottom',
    title: 'Approve when it’s ready',
    body: 'Set the PDF to Approved once it’s good to go — or reopen it. Activity shows who did what.',
  },
  {
    selector: '[data-tour="pdf-compare"]',
    placement: 'bottom',
    title: 'Compare versions',
    body: 'Put this round next to a previous one to see exactly what changed.',
  },
  {
    selector: '[data-tour="pdf-newversion"]',
    placement: 'bottom',
    title: 'Upload a new round',
    body: 'Got a revised PDF? Upload it as a new version — comments and approvals track per round.',
  },
];

/**
 * First-time, on-page walkthrough of the PDF review tools: a spotlight + callout
 * anchored to the real controls (toolbar, comments, Approve/Compare/New version).
 * Steps whose target isn't present (e.g. Compare with no earlier round, or
 * editor-only buttons for a reviewer) are skipped. Persisted client-side.
 */
export function PdfTour({ enabled }: { enabled: boolean }) {
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  // Start once — when the page is ready, not seen before, on a large screen, and
  // at least one target actually exists.
  useEffect(() => {
    if (!enabled || steps) return;
    if (localStorage.getItem(STORAGE_KEY) || window.innerWidth < 768) return;
    const t = setTimeout(() => {
      const present = STEPS.filter((s) => document.querySelector(s.selector));
      if (present.length > 0) {
        setSteps(present);
        setI(0);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [enabled, steps]);

  const step = steps?.[i];

  // Keep the highlight aligned as the layout settles / the window changes.
  useLayoutEffect(() => {
    if (!step) return;
    const measure = () => {
      const el = document.querySelector(step.selector);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step]);

  if (!steps || !step) return null;

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setSteps(null);
  };
  const last = i >= steps.length - 1;
  const callout = calloutPos(rect, step.placement);

  return (
    <div className="fixed inset-0 z-[130]">
      {/* Spotlight: the hole is the target; the giant box-shadow dims everything
          else. A transparent catcher above it blocks the app during the tour. */}
      {rect ? (
        <div
          className="pointer-events-none absolute rounded-lg transition-all"
          style={{
            top: Math.max(0, rect.top - PAD),
            left: Math.max(0, rect.left - PAD),
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-black/55" />
      )}
      <div className="absolute inset-0" />

      <div
        className="absolute rounded-xl bg-white p-4 shadow-xl"
        style={{ top: callout.top, left: callout.left, width: CALLOUT_W }}
      >
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
            {i + 1} of {steps.length}
          </span>
          <button
            type="button"
            onClick={finish}
            className="text-xs text-neutral-400 hover:text-neutral-700"
          >
            Skip
          </button>
        </div>
        <h3 className="text-sm font-semibold text-neutral-900">{step.title}</h3>
        <p className="mt-1 text-[13px] leading-relaxed text-neutral-600">{step.body}</p>
        <div className="mt-4 flex items-center justify-end gap-2">
          {i > 0 && (
            <button
              type="button"
              onClick={() => setI((n) => Math.max(0, n - 1))}
              className="rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-50"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={() => (last ? finish() : setI((n) => n + 1))}
            className="rounded-md bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-neutral-700"
          >
            {last ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Position the callout beside the target, clamped inside the viewport. */
function calloutPos(rect: DOMRect | null, placement: Step['placement']): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) return { top: vh / 2 - CALLOUT_H / 2, left: vw / 2 - CALLOUT_W / 2 };
  let top: number;
  let left: number;
  switch (placement) {
    case 'left':
      left = rect.left - CALLOUT_W - 16;
      top = rect.top + rect.height / 2 - CALLOUT_H / 2;
      break;
    case 'right':
      left = rect.right + 16;
      top = rect.top + rect.height / 2 - CALLOUT_H / 2;
      break;
    case 'top':
      left = rect.left + rect.width / 2 - CALLOUT_W / 2;
      top = rect.top - CALLOUT_H - 16;
      break;
    default:
      left = rect.left + rect.width / 2 - CALLOUT_W / 2;
      top = rect.bottom + 16;
  }
  left = Math.min(Math.max(8, left), vw - CALLOUT_W - 8);
  top = Math.min(Math.max(8, top), vh - CALLOUT_H - 8);
  return { top, left };
}
